// A sample "Profile View" dashboard for the landing-page hero: overall index ring, competency bars, six dimensions and a
// DiSC wheel. Pure SVG/CSS, no data fetching. The person and every number are ILLUSTRATIVE and labelled as such.
import { T } from "./glass.jsx";

const NAV = [["⌂", "Profile View", true], ["▦", "Competency Map"], ["▤", "Detailed Report"], ["◎", "Development Plan"], ["⚇", "Compare"], ["▥", "Action Plan"]];
const STRENGTHS = [["Interpersonal Influence", 90, "#6d2fd0", "#9b6bff"], ["Execution & Drive", 86, "#b9822e", "#d9b36f"], ["Emotional Awareness", 84, "#0e9f8e", "#4cc7b8"], ["Adaptability", 80, "#1f63c6", "#5b93e6"], ["Strategic Thinking", 78, "#c8a469", "#e3cfa6"]];
const DIMS = [
  ["Cognitive", 78, "#b9822e", "🧠", "Thinking · Reasoning"], ["Emotional", 84, "#0e9f8e", "♡", "Self-Awareness · Agility"], ["Values", 76, "#d9483f", "❦", "Purpose · Ethics"],
  ["Interpersonal", 90, "#6d2fd0", "⚇", "Social Intelligence"], ["Execution", 86, "#b9822e", "⚙", "Drive · Discipline"], ["Motivation", 80, "#1f63c6", "🔥", "Initiative · Resilience"],
];

function Card({ title, children, style }) {
  return (
    <div style={{ background: "rgba(255,255,255,.82)", border: "1px solid rgba(255,255,255,.95)", borderRadius: 14, padding: "14px 16px", boxShadow: "0 4px 16px rgba(13,27,54,.06)", ...style }}>
      {title && <div className="serif" style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, marginBottom: 10 }}>{title}</div>}
      {children}
    </div>
  );
}

function Ring({ value }) {
  const r = 46, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 120 120" width="128" height="128" role="img" aria-label={`Overall index ${value}`}>
      <circle cx="60" cy="60" r={r} fill="none" stroke="#e6ebf1" strokeWidth="10"/>
      <circle cx="60" cy="60" r={r} fill="none" stroke="#0e9f8e" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${c * value / 100} ${c}`} transform="rotate(-90 60 60)"/>
      <text x="60" y="62" textAnchor="middle" className="serif" style={{ fontSize: 34, fontWeight: 700, fill: T.ink }}>{value}</text>
      <text x="60" y="80" textAnchor="middle" style={{ fontSize: 8.5, fill: T.mute, fontWeight: 600 }}>Overall Index</text>
    </svg>
  );
}

function DiscWheel() {
  const q = [["D", "Dominance", "#d9483f", 68, "M60 60 L60 6 A54 54 0 0 0 6 60 Z"], ["i", "Influence", "#e8a020", 82, "M60 60 L114 60 A54 54 0 0 0 60 6 Z"], ["S", "Steadiness", "#2e9b5f", 76, "M60 60 L60 114 A54 54 0 0 0 114 60 Z"], ["C", "Conscientiousness", "#1f63c6", 74, "M60 60 L6 60 A54 54 0 0 0 60 114 Z"]];
  const pos = [[33, 38], [87, 38], [87, 86], [33, 86]];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <svg viewBox="0 0 120 120" width="108" height="108" role="img" aria-label="DiSC style wheel">
        {q.map(([l, n, c, , d], i) => <path key={l} d={d} fill={c} stroke="#fff" strokeWidth="2"/>)}
        {q.map(([l, n], i) => (
          <g key={l}><text x={pos[i][0]} y={pos[i][1]} textAnchor="middle" className="serif" style={{ fontSize: 17, fontWeight: 700, fill: "#fff" }}>{l}</text>
            <text x={pos[i][0]} y={pos[i][1] + 9} textAnchor="middle" style={{ fontSize: 5.2, fill: "#fff", fontWeight: 600 }}>{n.length > 12 ? "Conscientious." : n}</text></g>
        ))}
      </svg>
      <div style={{ display: "grid", gap: 5 }}>
        {q.map(([l, , c, v]) => <div key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: T.ink2, fontWeight: 600 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: c }}/>{l}<span style={{ marginLeft: "auto", color: T.mute, fontWeight: 700, paddingLeft: 12 }}>{v}</span></div>)}
      </div>
    </div>
  );
}

export default function ProfilePreview() {
  return (
    <div className="glass" aria-label="Sample profile dashboard (illustrative data)" role="group" style={{ display: "grid", gridTemplateColumns: "auto 1fr", overflow: "hidden", padding: 0 }}>
      <div className="pv-side" style={{ width: 138, background: "linear-gradient(180deg,#10223f,#0b1830)", padding: "16px 0", display: "flex", flexDirection: "column", gap: 2 }}>
        {NAV.map(([ic, l, on]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 14px", fontSize: 11, fontWeight: 600, color: on ? "#fff" : "rgba(255,255,255,.62)", background: on ? "rgba(200,150,78,.22)" : "transparent", borderLeft: `3px solid ${on ? "#c8964e" : "transparent"}` }}>
            <span style={{ fontSize: 13, color: on ? "#e0b36b" : "rgba(255,255,255,.5)" }}>{ic}</span>{l}
          </div>
        ))}
      </div>
      <div style={{ padding: 14, display: "grid", gap: 12, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: "linear-gradient(135deg,#d4a65e,#9b6bff)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15 }}>EC</div>
            <div>
              <div className="serif" style={{ fontSize: 19, fontWeight: 700, color: T.ink, lineHeight: 1.1 }}>Emily Carter</div>
              <div style={{ fontSize: 9, letterSpacing: 2, fontWeight: 700, color: T.gold, marginTop: 3 }}>MANAGER – OPERATIONS</div>
            </div>
          </div>
          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: T.mute, border: "1px dashed rgba(13,27,54,.25)", borderRadius: 99, padding: "3px 10px" }}>Sample · illustrative data</span>
        </div>

        <div className="pv-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 12 }}>
          <Card title="AXIS360 Competency Profile">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center" }}>
              <Ring value={82}/>
              <div style={{ fontSize: 10.5, color: T.mute, lineHeight: 1.55 }}>A balanced profile with strong interpersonal capability and high execution drive.</div>
            </div>
          </Card>
          <Card title="Competency Strengths">
            <div style={{ display: "grid", gap: 7 }}>
              {STRENGTHS.map(([n, v, c1, c2], i) => (
                <div key={n} style={{ display: "grid", gridTemplateColumns: "16px 1fr 24px", gap: 7, alignItems: "center", fontSize: 10.5, color: T.ink2 }}>
                  <span style={{ width: 16, height: 16, borderRadius: "50%", background: c1, color: "#fff", fontSize: 8.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                  <div><div style={{ marginBottom: 2 }}>{n}</div><div style={{ height: 6, borderRadius: 99, background: "#e8edf3" }}><div style={{ width: `${v}%`, height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${c1},${c2})` }}/></div></div>
                  <span style={{ fontWeight: 700, textAlign: "right" }}>{v}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card title="Six Human Dimensions">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 6 }}>
            {DIMS.map(([n, v, c, ic, sub]) => (
              <div key={n} style={{ textAlign: "center", minWidth: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: c, color: "#fff", margin: "0 auto 4px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{ic}</div>
                <div style={{ fontSize: "clamp(7px,2vw,9.5px)", fontWeight: 700, color: c, whiteSpace: "nowrap" }}>{n}</div>
                <div className="serif" style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>{v}</div>
                <div style={{ display: "grid", gap: 2, margin: "3px auto", width: "58%" }}>
                  {Array.from({ length: 7 }, (_, i) => <div key={i} style={{ height: 4, borderRadius: 2, background: i >= 7 - Math.round(v / 100 * 7) ? c : "#e8edf3" }}/>)}
                </div>
                <div style={{ fontSize: 7.5, color: T.faint, lineHeight: 1.3 }}>{sub}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Behavioural Style (DiSC)"><DiscWheel/></Card>
      </div>
    </div>
  );
}
